import {LoadingSection} from '#/atoms/loading-section';
import {WishlistButton} from '#/components/buttons/wishlist-button';
import {LandMarketPriceGraph} from '#/components/graphs/land-market-price-graph';
import {LandMarketIndicator} from '#/components/indicators/market-indicator';
import {landsTableSortableColumnData} from '#/components/lands-table/column-data';
import {LandThumbnail} from '#/components/lands/land-thumbnail';
import {CoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {getNumberPositivityStatus} from '#/utils/number-utils';
import {FunctionComponent} from 'react';
import {useTranslation} from 'react-i18next';
import {Land, LandMarketInfo} from 'shareland-common';

type LandTableRowProps = {
  onLand: (land: Land, landMarketInfo: LandMarketInfo | undefined) => void;
  displayPref: CoinDisplayPreferences;
  isTableSticky: boolean;
  selected: boolean;
  landAndInfo: {land: Land; landMarketInfo: LandMarketInfo | undefined};
};
export const LandTableRow: FunctionComponent<LandTableRowProps> = ({
  onLand,
  selected,
  landAndInfo,
  isTableSticky,
  displayPref,
}) => {
  const {t} = useTranslation('translation');

  const {land, landMarketInfo} = landAndInfo;

  const landName = landsTableSortableColumnData.land.getValue(landAndInfo);
  const price = landsTableSortableColumnData.price.getValue(landAndInfo);
  const premium = landsTableSortableColumnData.premium.getValue(landAndInfo);
  const tvl = landsTableSortableColumnData.tvl.getValue(landAndInfo);
  const volume = landsTableSortableColumnData.volume.getValue(landAndInfo);
  const marketCap = landsTableSortableColumnData.marketCap.getValue(landAndInfo);

  const onClick = () => {
    if (!!land) {
      onLand(land, landMarketInfo);
    }
  };

  return (
    <tr
      className={'cursor-pointer hover:bg-gray-300 transition-colors duration-200 group font-normal h-[110px]'}
      onClick={onClick}
    >
      <td
        className={`sticky bg-white group-hover:bg-gray-300 p-0 transition-colors duration-200 left-0 ${
          isTableSticky ? 'before:right-box-shadow' : ''
        } relative`}
      >
        {selected && <span className='absolute top-0 left-0 z-20 h-full w-[2px] bg-deepblue' />}

        <div className='flex gap-3 items-stretch p-6 pl-4'>
          <WishlistButton
            style={{
              starStrokeUnselected: 'stroke-gray-500',
              starStrokeUnselectedHover: true,
              iconWidth: 'w-5',
            }}
            className='w-7'
            land={land}
            landMarketInfo={landMarketInfo}
          />

          <LandThumbnail
            land={land}
            className={
              'w-[60px] aspect-square rounded-lg object-cover transition-transform duration-500 hidden @lg:block'
            }
          />
          <div className='flex flex-col justify-center items-start overflow-hidden'>
            <div className='w-full text-black font-bold text-left truncate'>
              {t('components.locationTable.columns.location.rowDisplay.title', {
                landName: landName,
              })}
            </div>
            <div className='w-full text-black font-light text-left truncate'>
              {t('components.locationTable.columns.location.rowDisplay.subtitle', {
                context: land.type === 'NEIGHBORHOOD' ? 'neighborhood' : 'city',
                landCity: land.city,
                landState: land.state,
              })}
            </div>
          </div>
        </div>
      </td>
      <td className='overflow-hidden whitespace-nowrap px-6'>
        {price !== undefined ? (
          t('components.locationTable.columns.price.rowDisplay', {
            currencySymbol: displayPref.symbol,
            amount: displayPref.displayAmount(price),
          })
        ) : (
          <LoadingSection w='w-20' h='h-4' />
        )}
      </td>
      <td className='overflow-hidden whitespace-nowrap px-6'>
        <LandMarketIndicator
          land={land}
          range='daily'
          landMarketInfo={landMarketInfo}
          showPrice={false}
          iconSize='sm'
        />
      </td>
      <td
        className={
          'overflow-hidden whitespace-nowrap px-6 data-[positivity="positive"]:text-upgreen data-[positivity="negative"]:text-downred'
        }
        data-positivity={getNumberPositivityStatus(premium ?? 0)}
      >
        {premium !== undefined ? (
          t('components.locationTable.columns.premium.rowDisplay', {value: premium})
        ) : (
          <LoadingSection w='w-24' h='h-4' />
        )}
      </td>
      <td className='overflow-hidden whitespace-nowrap px-6'>
        {t('components.locationTable.columns.tvl.rowDisplay', {
          currencySymbol: displayPref.symbol,
          amount: displayPref.displayAmount(tvl),
        })}
      </td>
      <td className='overflow-hidden whitespace-nowrap px-6'>
        {t('components.locationTable.columns.volume.rowDisplay', {
          currencySymbol: displayPref.symbol,
          amount: displayPref.displayAmount(volume),
        })}
      </td>
      <td className='overflow-hidden whitespace-nowrap px-6'>
        {marketCap !== undefined ? (
          t('components.locationTable.columns.marketCap.rowDisplay', {
            currencySymbol: displayPref.symbol,
            amount: displayPref.displayAmount(marketCap),
          })
        ) : (
          <LoadingSection w='w-24' h='h-4' />
        )}
      </td>
      <td className='px-6'>
        <LandMarketPriceGraph land={land} size='large' graphRange='day' />
      </td>
    </tr>
  );
};
